import CanadaFlag from '@src/common/assets/canada-flag-icon.svg?react';
import UsaFlag from '@src/common/assets/usa-flag-icon.svg?react';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { prefixFieldNameIfPrefixExists } from '@src/common/utils/formUtils';
import { ConditionalField } from '@src/orders/PermitFormSections/ConditionalField';
import { ControllerFormTextInput } from '@src/orders/PermitFormSections/ControllerFormTextInput';
import { useDisabledFields } from '@src/orders/PermitFormSections/useDisabledFields';
import type { jurisdictionIdsFields } from '@src/permitField/JurisdictionIdNumberFields';
import {
  abMVIDNumberField,
  bcCustomerNumber,
  bcFINNumber,
  caNSC,
  federalIdNumber,
  kentuckyNumber,
  laNumber,
  montanaNumber,
  nlMasterNumber,
  onCVORNumber,
  onPRIOAccountNumber,
  oregonNumber,
  qcNEQNumber,
  qcNIRNumber,
  tennesseeNumber,
  texasNumber,
  usDotNumber,
  usICC,
  usIFTA,
  usIRP,
} from '@src/permitField/JurisdictionIdNumberFields';
import type { ReactNode } from 'react';
import type { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { StringSchema } from 'yup';

import { ClientFormSection } from './ClientFormSection';

interface Props {
  control: Control;
  fields: (keyof typeof jurisdictionIdsFields)[];
  fieldsPrefix?: string;
}

type SubSection = {
  title?: string;
  fields: Field[];
};

type Section = {
  key: string;
  flag: ReactNode;
  subSection: SubSection[];
};

type Field = {
  fieldName: string;
  validator: StringSchema;
  placeholder?: string;
};

export const JurisdictionIdNumbersForm = ({
  fields,
  control,
  fieldsPrefix,
}: Props) => {
  const { t } = useTranslation();
  const { isFieldDisabled } = useDisabledFields();
  const sections: Section[] = [
    {
      key: t('canada'),
      flag: <CanadaFlag />,
      subSection: [
        { fields: [caNSC] },
        { title: 'AB', fields: [abMVIDNumberField] },
        {
          title: 'BC',
          fields: [
            { ...bcCustomerNumber, placeholder: '000000000' },
            {
              ...bcFINNumber,
              placeholder: t('formatAOrFormatB', {
                formatA: '00000',
                formatB: '000000',
              }),
            },
          ],
        },
        {
          title: 'QC',
          fields: [qcNEQNumber, qcNIRNumber],
        },
        {
          title: 'ON',
          fields: [
            { ...onCVORNumber },
            { ...onPRIOAccountNumber, placeholder: '00000' },
          ],
        },
        { title: 'NL', fields: [nlMasterNumber] },
      ],
    },
    {
      key: t('canadaAndUsa'),
      flag: (
        <>
          <CanadaFlag /> <UsaFlag />
        </>
      ),
      subSection: [{ fields: [usICC, usIFTA, usIRP] }],
    },
    {
      key: t('usa'),
      flag: <UsaFlag />,
      subSection: [
        {
          fields: [
            { ...usDotNumber, placeholder: t('usDotNumberFormat') },
            { ...federalIdNumber, placeholder: '00-0000000' },
          ],
        },
        { title: 'KY', fields: [kentuckyNumber] },
        { title: 'LA', fields: [laNumber] },
        { title: 'MT', fields: [montanaNumber] },
        { title: 'OR', fields: [oregonNumber] },
        { title: 'TN', fields: [{ ...tennesseeNumber, placeholder: '00000' }] },
        { title: 'TX', fields: [texasNumber] },
      ],
    },
  ];

  const shouldDisplay = ({ fields: sectionFields }: SubSection) => {
    return sectionFields.find((sectionField) =>
      fields.includes(sectionField.fieldName),
    );
  };

  return (
    <Stack direction="column" gap={theme.spacing['8']}>
      {sections.map(({ key, flag, subSection }, sectionIndex) => {
        if (!subSection.some(shouldDisplay)) {
          return null;
        }
        return (
          <Stack
            direction="column"
            gap={theme.spacing['5']}
            key={`${key}-${sectionIndex}`}
          >
            <Stack
              flexDirection="row"
              gap={theme.spacing[1]}
              alignItems="center"
            >
              {flag}
              <Typography variant="subtitle1">{key}</Typography>
            </Stack>

            {subSection.map((subSection: SubSection, subSectionIndex) => {
              if (!shouldDisplay(subSection)) {
                return null;
              }

              return (
                <ConditionalSection
                  title={subSection.title}
                  key={`${subSection.title}-${subSectionIndex}`}
                >
                  {subSection.fields.map((field, fieldIndex) => (
                    <ConditionalField
                      key={`${field.fieldName}-${fieldIndex}`}
                      fields={fields}
                      conditions={field.fieldName}
                    >
                      <ControllerFormTextInput
                        control={control}
                        className={textInputStyle}
                        fieldName={prefixFieldNameIfPrefixExists(
                          field.fieldName,
                          fieldsPrefix,
                        )}
                        label={t(`fields.${field.fieldName}`)}
                        placeholder={field.placeholder}
                        disabled={isFieldDisabled(field.fieldName)}
                      />
                    </ConditionalField>
                  ))}
                </ConditionalSection>
              );
            })}
          </Stack>
        );
      })}
    </Stack>
  );
};

const textInputStyle = css`
  width: 100%;

  @media (min-width: 900px) {
    width: 100%;
  }

  @media (min-width: 1200px) {
    width: 50%;
  }
`;

const ConditionalSection = ({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) =>
  title ? (
    <ClientFormSection title={title}>
      <Stack flexDirection="row" spacing={theme.spacing[8]}>
        {children}
      </Stack>
    </ClientFormSection>
  ) : (
    children
  );
